import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppConfig } from "~/app/config/useAppConfig";
import { isLineLevelCustomerOrderNumberEnabled } from "~/app/config/featureFlags";
import { useCommerceContext } from "~/app/config/useSession";
import { isSessionDuplicateInvariantError, pageDataKeys, sessionQueryOptions } from "~/app/api/pageData";
import { useSession } from "~/app/providers/SessionProvider";
import type { AppDispatch } from "~/app/store";
import type { SessionCartIdentityData } from "~/app/store/bffApi";
import type { PriceReferenceSelection } from "~/domains/priceReference/types";
import { Alert, Button, Stack } from "~/common/mui";
import type { ActiveMergeCartFlowPhase } from "~/domains/cart/mergeCartFlow";
import { minicartApi } from "~/features/cart/minicart/minicartApi";
import { applyMergeCartSuccess, invalidateMinicartProjection } from "~/features/cart/minicart/minicartRedux";
import { addMessage } from "~/features/toasts/redux/actions";
import { redirectToUrl, resolveCartPageUrl } from "~/utils/url/urlUtils";
import {
  cartMergeOperations,
  isCartAlreadyExistsForContext,
  isCartContextConflict,
  type CartMergeOperations,
  type CartMergePreviewContextWsDTO,
} from "../api/cartMergeApi";
import type {
  BulkUploadAddResponse,
  BulkUploadEntryIntentWsDTO,
  BulkUploadPreviewProduct,
  BulkUploadSuccessWsDTO,
} from "../types/contracts";
import type { BulkUploadWorkflowFailure } from "../workflow/bulkUploadReducer";
import { BulkUploadDialogRoot } from "./BulkUploadDialogRoot";
import { BulkUploadLoading } from "./BulkUploadLoading";
import { BulkUploadPreview } from "./BulkUploadPreview";
import { useTranslation } from "react-i18next";

type MergePhase =
  | "loadingPreview"
  | "preview"
  | "previewFailed"
  | "creating"
  | "merging"
  | "discarding"
  | "duplicateInvariant";

export type MergeCartStateOutcome =
  | { kind: "discarded" }
  | { kind: "mergeSuccess"; productAmount: number }
  | { kind: "mergeUncertain" };

export interface MergeCartSessionSnapshot {
  activeCart?: SessionCartIdentityData | null;
}

export interface MergeCartDialogCoordinatorProps {
  bulkMaxRows?: string;
  customerOrderNumberEnabled: boolean;
  priceReferenceSelection?: PriceReferenceSelection;
  initialSession: MergeCartSessionSnapshot | null;
  invalidateCartState: (outcome: MergeCartStateOutcome) => Promise<void>;
  notifyTerminalError: () => void;
  onClose: () => void;
  onLifecycleChange: (phase: ActiveMergeCartFlowPhase) => void;
  onSuccess: (success: BulkUploadSuccessWsDTO) => void;
  operations: CartMergeOperations;
  refreshSession: () => Promise<MergeCartSessionSnapshot>;
  sourceCartGuid: string;
  targetPriceEnabled: boolean;
  webservicesDisabled: boolean;
}

export function MergeCartDialogCoordinator({
  bulkMaxRows,
  customerOrderNumberEnabled,
  priceReferenceSelection,
  initialSession,
  invalidateCartState,
  notifyTerminalError,
  onClose,
  onLifecycleChange,
  onSuccess,
  operations,
  refreshSession,
  sourceCartGuid,
  targetPriceEnabled,
  webservicesDisabled,
}: MergeCartDialogCoordinatorProps) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const previewAbortController = useRef<AbortController>();
  const capturedSourceCartGuid = useRef(sourceCartGuid);
  const activeCartCode = useRef(initialSession?.activeCart?.code);
  const previewStarted = useRef(false);
  const closed = useRef(false);
  const terminalActionInFlight = useRef(false);
  const [phase, setPhase] = useState<MergePhase>("loadingPreview");
  const [products, setProducts] = useState<BulkUploadPreviewProduct[]>([]);
  const [previewContext, setPreviewContext] = useState<CartMergePreviewContextWsDTO>();
  const [rowErrors, setRowErrors] = useState<BulkUploadAddResponse["rowErrors"]>([]);
  const [failure, setFailure] = useState<BulkUploadWorkflowFailure>();
  const [saleOnly, setSaleOnly] = useState(false);

  const requestPreview = useCallback(async () => {
    const sourceGuid = capturedSourceCartGuid.current;
    if (!sourceGuid) {
      setFailure({ kind: "business" });
      setPhase("previewFailed");
      return;
    }

    previewAbortController.current?.abort();
    const controller = new AbortController();
    previewAbortController.current = controller;
    setFailure(undefined);
    setPhase("loadingPreview");

    try {
      const response = await operations.preview(sourceGuid, controller.signal);
      if (controller.signal.aborted || closed.current) {
        return;
      }
      if (response.outcome === "PREVIEW" && response.previewContext) {
        setProducts(response.products);
        setPreviewContext(response.previewContext);
        setRowErrors(response.rowErrors);
        setPhase("preview");
        return;
      }
      setFailure(responseFailure(response));
      setRowErrors(response.rowErrors);
      setPhase("previewFailed");
    } catch (error) {
      if (!controller.signal.aborted && !isAbortError(error) && !closed.current) {
        setFailure({ kind: "transport" });
        setPhase("previewFailed");
      }
    } finally {
      if (previewAbortController.current === controller) {
        previewAbortController.current = undefined;
      }
    }
  }, [operations]);

  useEffect(() => {
    if (!previewStarted.current) {
      previewStarted.current = true;
      void requestPreview();
    }
    return () => {
      closed.current = true;
      previewAbortController.current?.abort();
    };
  }, [requestPreview]);

  const refreshActiveCart = useCallback(async () => {
    const refreshed = await refreshSession();
    activeCartCode.current = refreshed.activeCart?.code;
    return activeCartCode.current;
  }, [refreshSession]);

  const finishTerminal = useCallback(
    async (options: { error: boolean; stateOutcome: MergeCartStateOutcome; success?: BulkUploadSuccessWsDTO }) => {
      onLifecycleChange("refreshing");
      try {
        await invalidateCartState(options.stateOutcome);
      } catch {
        // Cache refresh is best-effort; the terminal cart operation must not become retryable.
      }
      if (options.error) {
        notifyTerminalError();
      }
      closed.current = true;
      onClose();
      if (options.success) {
        onSuccess(options.success);
      }
    },
    [invalidateCartState, notifyTerminalError, onClose, onLifecycleChange, onSuccess],
  );

  const markDuplicateInvariant = useCallback(() => {
    terminalActionInFlight.current = false;
    setFailure({ kind: "business" });
    setPhase("duplicateInvariant");
  }, []);

  const surfaceDuplicateInvariant = useCallback(async () => {
    try {
      await refreshActiveCart();
      terminalActionInFlight.current = false;
      setFailure({ kind: "business" });
      setPhase("preview");
    } catch (error) {
      if (isSessionDuplicateInvariantError(error)) {
        markDuplicateInvariant();
        return;
      }
      terminalActionInFlight.current = false;
      setFailure({ kind: "transport" });
      setPhase("preview");
    }
  }, [markDuplicateInvariant, refreshActiveCart]);

  const recoverFromCreateConflict = useCallback(async () => {
    try {
      await refreshActiveCart();
    } catch (error) {
      if (isSessionDuplicateInvariantError(error)) {
        markDuplicateInvariant();
        return;
      }
      activeCartCode.current = undefined;
    }
    terminalActionInFlight.current = false;
    setFailure({ kind: "business" });
    setPhase("preview");
  }, [markDuplicateInvariant, refreshActiveCart]);

  const submit = useCallback(
    async (entries: BulkUploadEntryIntentWsDTO[]) => {
      const sourceGuid = capturedSourceCartGuid.current;
      if (
        terminalActionInFlight.current ||
        phase !== "preview" ||
        !sourceGuid ||
        !previewContext ||
        entries.length === 0
      ) {
        return;
      }

      terminalActionInFlight.current = true;
      setFailure(undefined);
      setRowErrors([]);
      let targetCode = activeCartCode.current;

      if (!targetCode) {
        setPhase("creating");
        onLifecycleChange("creatingTarget");
        try {
          const createdCart = await operations.createCart();
          targetCode = createdCart.code;
          activeCartCode.current = createdCart.code;
          await refreshSession();
        } catch (error) {
          if (isCartAlreadyExistsForContext(error)) {
            await recoverFromCreateConflict();
            onLifecycleChange("preview");
            return;
          }
          if (isSessionDuplicateInvariantError(error)) {
            markDuplicateInvariant();
            onLifecycleChange("preview");
            return;
          }
          terminalActionInFlight.current = false;
          setFailure({ kind: "transport" });
          setPhase("preview");
          onLifecycleChange("preview");
          return;
        }
      }

      setPhase("merging");
      onLifecycleChange("merging");
      try {
        const response = await operations.merge(targetCode, sourceGuid, {
          previewContext,
          entries,
        });
        if (closed.current) {
          return;
        }
        if (response.outcome === "SUCCESS" && response.success) {
          await finishTerminal({
            error: false,
            stateOutcome: {
              kind: "mergeSuccess",
              productAmount: response.success.productAmount,
            },
            success: response.success,
          });
          return;
        }
        if (response.outcome === "CONTEXT_CHANGED") {
          terminalActionInFlight.current = false;
          setProducts([]);
          setPreviewContext(undefined);
          setRowErrors([]);
          setSaleOnly(false);
          await requestPreview();
          onLifecycleChange("preview");
          return;
        }
        if (isRecoverableResponse(response)) {
          if (response.products.length > 0 || response.outcome === "VALIDATION_FAILED") {
            setProducts(response.products);
          }
          setRowErrors(response.rowErrors);
          setFailure(responseFailure(response));
          terminalActionInFlight.current = false;
          setPhase("preview");
          onLifecycleChange("preview");
          return;
        }
        await finishTerminal({
          error: true,
          stateOutcome: { kind: "mergeUncertain" },
        });
      } catch (error) {
        if (isCartContextConflict(error)) {
          await surfaceDuplicateInvariant();
          onLifecycleChange("preview");
          return;
        }
        try {
          await operations.discard(sourceGuid);
        } catch {
          // The add outcome is ambiguous after dispatch. Only idempotent discard is safe; merge is never retried.
        }
        await finishTerminal({
          error: true,
          stateOutcome: { kind: "mergeUncertain" },
        });
      }
    },
    [
      finishTerminal,
      operations,
      onLifecycleChange,
      markDuplicateInvariant,
      phase,
      previewContext,
      recoverFromCreateConflict,
      refreshSession,
      requestPreview,
      surfaceDuplicateInvariant,
    ],
  );

  const discard = useCallback(async () => {
    const sourceGuid = capturedSourceCartGuid.current;
    if (terminalActionInFlight.current) {
      return;
    }
    if (!sourceGuid) {
      closed.current = true;
      onClose();
      return;
    }

    terminalActionInFlight.current = true;
    previewAbortController.current?.abort();
    setPhase("discarding");
    let failed = false;
    try {
      await operations.discard(sourceGuid);
    } catch {
      failed = true;
    }
    await finishTerminal({
      error: failed,
      stateOutcome: { kind: "discarded" },
    });
  }, [finishTerminal, onClose, operations]);

  const terminalPending = phase === "creating" || phase === "merging" || phase === "discarding";
  return (
    <BulkUploadDialogRoot
      closeDisabled={terminalPending}
      contentRef={contentRef}
      onClose={() => void discard()}
      title={t("feature.mergeCart.popup.title")}
    >
      {phase === "loadingPreview" ? (
        <Stack gap={3}>
          <BulkUploadLoading operation="preview" />
          <Button data-testid="bulk-upload-btn-cancel" variant="text" onClick={() => void discard()}>
            {t("bulkUpload.buttons.cancel")}
          </Button>
        </Stack>
      ) : phase === "previewFailed" || phase === "duplicateInvariant" ? (
        <Stack gap={2}>
          <Alert data-testid="bulk-upload-preview-failure" severity="error">
            {t(failure?.error?.reason ?? "bulkUpload.error.webservice")}
          </Alert>
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button variant="text" onClick={() => void discard()}>
              {t("bulkUpload.buttons.cancel")}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <BulkUploadPreview
          bulkMaxRows={bulkMaxRows}
          customerOrderNumberEnabled={customerOrderNumberEnabled}
          disabled={webservicesDisabled}
          failure={failure}
          priceReferenceSelection={priceReferenceSelection}
          onCancel={() => void discard()}
          onLastRowRemoved={() => {
            setProducts([]);
            setRowErrors([]);
            setSaleOnly(false);
          }}
          onOverrideConfirmationClose={() => undefined}
          onOverrideConfirmationOpen={() => undefined}
          onSaleOnlyChange={setSaleOnly}
          onSubmit={(entries) => void submit(entries)}
          overrideConfirmationOpen={false}
          pending={terminalPending}
          products={products}
          rowErrors={rowErrors}
          saleOnly={saleOnly}
          scrollContainerRef={contentRef}
          showOverrideAction={false}
          targetPriceEnabled={targetPriceEnabled}
          terminalActionsDisabled={terminalPending}
        />
      )}
    </BulkUploadDialogRoot>
  );
}

export function MergeCartDialog({
  flow,
  onClose,
}: {
  flow: {
    sourceCartGuid: string;
    onLifecycleChange: (phase: ActiveMergeCartFlowPhase) => void;
  };
  onClose: () => void;
}) {
  const session = useSession();
  const configuration = useAppConfig()?.configuration;
  const commerceContext = useCommerceContext();
  const queryClient = useQueryClient();
  const dispatch = useDispatch<AppDispatch>();

  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.session(),
    });
    return queryClient.fetchQuery(sessionQueryOptions());
  }, [queryClient]);

  const invalidateCartState = useCallback(
    (outcome: MergeCartStateOutcome) => invalidateMergeCartClientState(dispatch, queryClient, outcome),
    [dispatch, queryClient],
  );

  const notifyTerminalError = useCallback(() => {
    dispatch(addMessage("error", "bulkUpload.error.webservice", "translation"));
  }, [dispatch]);

  const handleSuccess = useCallback((success: BulkUploadSuccessWsDTO) => {
    const params = new URLSearchParams({
      bulkSuccess: success.productAmount.toString(),
      quantityAdjusted: success.quantityAdjusted.toString(),
    });
    redirectToUrl(`${resolveCartPageUrl()}?${params.toString()}`);
  }, []);

  return (
    <MergeCartDialogCoordinator
      bulkMaxRows={configuration?.bulkMaxRows}
      customerOrderNumberEnabled={isLineLevelCustomerOrderNumberEnabled()}
      priceReferenceSelection={commerceContext?.priceReferenceSelection}
      initialSession={session}
      invalidateCartState={invalidateCartState}
      notifyTerminalError={notifyTerminalError}
      onClose={onClose}
      onLifecycleChange={flow.onLifecycleChange}
      onSuccess={handleSuccess}
      operations={cartMergeOperations}
      refreshSession={refreshSession}
      sourceCartGuid={flow.sourceCartGuid}
      targetPriceEnabled={commerceContext?.targetPriceEnabled ?? false}
      webservicesDisabled={configuration?.disableAllWS ?? false}
    />
  );
}

export async function invalidateMergeCartClientState(
  dispatch: AppDispatch,
  queryClient: QueryClient,
  outcome: MergeCartStateOutcome,
) {
  dispatch(invalidateMinicartProjection());
  dispatch(minicartApi.util.invalidateTags(["Minicart"]));
  if (outcome.kind === "mergeSuccess") {
    dispatch(applyMergeCartSuccess(outcome.productAmount));
  }
  await Promise.all([
    queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.session(),
    }),
    queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.page("cart"),
    }),
  ]);
}

function isRecoverableResponse(response: BulkUploadAddResponse): boolean {
  return response.outcome === "VALIDATION_FAILED" || response.outcome === "INVALID_REQUEST";
}

function responseFailure(response: BulkUploadAddResponse | { globalError?: BulkUploadAddResponse["globalError"] }) {
  const outcome = "outcome" in response ? response.outcome : undefined;
  const kind: BulkUploadWorkflowFailure["kind"] =
    outcome === "VALIDATION_FAILED" || outcome === "INVALID_REQUEST"
      ? "validation"
      : outcome === "BUSINESS_ERROR"
      ? "business"
      : "upload";
  return {
    kind,
    ...(response.globalError ? { error: response.globalError } : {}),
    ...("rowErrors" in response && response.rowErrors.length > 0 ? { rowErrors: response.rowErrors } : {}),
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
